/**
 * Amplified-prompt parsing — shared by the Result page and the gallery
 * lightbox. Claude returns the amplified prompt as labelled paragraphs
 * ("Scene: …", "Lighting: …"); this splits them into sections for a clean,
 * typographic render. Falls back to one unlabelled block if no labels match.
 */

export interface PromptSection {
  label: string | null;
  body: string;
}

export const SECTION_LABELS = [
  'Scene',
  'Pose',
  'Inscription / Speech bubble / Sign / Caption',
  'Inscription / Speech bubble / Sign',
  'Inscription',
  'Speech bubble',
  'Sign',
  'Caption',
  'Lighting and atmosphere',
  'Lighting',
  'Atmosphere',
  'Environment',
  'Style',
  'Composition',
  'Important',
] as const;

export function parseAmplified(text: string): PromptSection[] {
  if (!text) return [];
  const escaped = SECTION_LABELS.map((l) => l.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&'));
  const pattern = new RegExp(`(?:^|\\n|(?<=[.!?]\\s))(${escaped.join('|')}):\\s*`, 'g');
  const matches: { label: string; start: number; bodyStart: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    matches.push({ label: m[1] ?? '', start: m.index, bodyStart: m.index + m[0].length });
  }
  if (matches.length === 0) return [{ label: null, body: text.trim() }];

  const sections: PromptSection[] = [];
  const leadin = text.slice(0, matches[0]!.start).trim();
  if (leadin) sections.push({ label: null, body: leadin });
  for (let i = 0; i < matches.length; i++) {
    const cur = matches[i]!;
    const next = matches[i + 1];
    const body = text.slice(cur.bodyStart, next ? next.start : text.length).trim();
    sections.push({ label: cur.label, body });
  }
  return sections;
}
